import { Mui } from '@osu/react-ui';

export default function MenuDialogBase({ apiCall, buttons, extraContent, menuItemAndClose = {}, result }) {
    const { menuItem = {}, close } = menuItemAndClose;
    const cancelOrCloseLabel = result?.isSuccess ? "Close" : "Cancel";
    const closeVariant = result?.isSuccess ? "contained" : "none";
    const outlineOnKeyboardFocus = { ":focus-visible": { "outline": "2pt solid #0060df", "border-radius": "2px" } };
    
    const closeAndRefreshIfNoLoad = () => closeAndRefresh(result.isLoading);
    function closeAndRefresh(isLoading) {
        if (isLoading) { return; } // Prevent mid-apiCall closings
        close();
    }
    
    return (
        <Mui.Dialog
            aria-label="" // Fix screen reader repeating title 4 times; DialogTitle and DialogContent ids must be set as well
            fullWidth maxWidth="sm" // Necessary for shorter messages
            onBackdropClick={closeAndRefreshIfNoLoad}
            open={true}
        >
            <AlertMessage result={result} />

            <Mui.DialogTitle tabIndex={0} id="alert-dialog-title">{menuItem.label}</Mui.DialogTitle>
            
            <Mui.DialogContent>
                {menuItem.message && <Mui.DialogContentText tabIndex={0} id="alert-dialog-description">{menuItem.message}</Mui.DialogContentText>}
                
                {extraContent}
            </Mui.DialogContent>
            
            <Mui.DialogActions className="padding-2">
                <Mui.Button disabled={result?.isLoading} onClick={closeAndRefreshIfNoLoad} size="small" variant={closeVariant} sx={{ minWidth: "6em", ...outlineOnKeyboardFocus }}>{cancelOrCloseLabel}</Mui.Button>
                {buttons?.map((button, index) => <DialogButton apiCall={apiCall} key={index} button={button} result={result} />) ?? <></> }
            </Mui.DialogActions>
        </Mui.Dialog>
    );
}

function AlertMessage({ result = {} }) {
    const { data, error, isError, isLoading, isSuccess } = result;
    if (isLoading || !(isError || isSuccess)) { return <></>; }

    // Soft, non-error outcome: the mutation resolved without a status but carried a reason (e.g. a cap like
    // AT_LIMIT). Treat it as info, not a false "Success". The owning dialog may also render its own detail.
    const softReason = !isError && !data?.status && data?.reason;

    // Check if lambda response includes a type field (info, success, error), otherwise default to success
    const severity = isError ? "error" : (softReason ? "info" : (data?.type ?? "success"));
    let message = isError
        ? (error.message ?? "Encountered error with last request.")
        : (data?.status ?? (softReason ? "This action could not be completed." : "Success"));
    
    return (
        <Mui.Alert aria-live="polite" autoFocus severity={severity} sx={theme => theme.applyStyles("dark", { backgroundColor: "#666" })} tabIndex="0">
            <Mui.Typography>{message}</Mui.Typography>
        </Mui.Alert>
    );
}

function DialogButton({ apiCall, button, result = {} }) {
    const { isLoading, isSuccess } = result;
    let { label, variables, variablesFn = () => {} } = button;
    if (isSuccess || !label) { return <></>; } // Hide button if successful
    
    variables = variables ?? variablesFn(); // Doesn't exist? Pull dynamically
    const announceChange = result?.isLoading ? "assertive" : "none";
    const buttonDescription = result?.isLoading ? "Processing" : undefined;
    
    async function apiCallFn() { apiCall(variables); }
    
    return (
        <Mui.Button
            aria-description={buttonDescription}
            aria-live={announceChange}
            id={label}
            disabled={isLoading || button.isDisabled}
            onClick={apiCallFn}
            size="small"
            style={{ marginRight: "0.5rem", minWidth: "6em" }}
            variant="contained">
                { isLoading && <Mui.CircularProgress aria-hidden="true" aria-description="" aria-label="Processing" size={20} style={{ float: "left" }} className="margin-right-2" /> }
                {label}
        </Mui.Button>
    );
}